import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import { LoadPickBonusCommand } from "slotworks/commands/comms/load-pick-bonus-command";
import Components from "slotworks/components/components";
import RequestSpin from "slotworks/state-machine/standard/states/request-spin";
import isMcSymbol from "utils/is-mc-symbol";
import { Services } from "slotworks/services/services";
import SpinReelTransition from "slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import settingsModel from "slotworks/model/settings/settings-model";
import gameState from "slotworks/model/game-state";
import BonusResult from "slotworks/model/gameplay/records/results/bonus-result";
import MCSpinRecord from "integration/builders/records/mc-spin-record";
import Sequence from "slotworks/utils/contracts/sequence";
import lightningBorderAnimationUtil from "utils/lightning-border-animation-util";
import { getCurrentPick } from "utils/calculate-picks-remaining";
import PromptContent from "slotworks/components/prompt/prompt-content";
import { HoldAndSpinRecord } from "ags/integration/records/hold-and-spin-record";

export default class CGMCBonusRequestSpin extends RequestSpin {

    public onEnter() {
        if (this.shouldShowRetriggerPrompt()) {
            this.showRetriggerPrompt().then(() => this.setupReelDelays());
        } else {
            this.setupReelDelays();
        }
    }

    protected setupReelDelays() {

        if (this.shouldHoldCurrentSymbols()) {
            this.holdCurrentSymbols();
        } else {
            lightningBorderAnimationUtil.stop();
        }

        const transition = Components.matrix.getTransition() as SpinReelTransition;
        transition.stopGap = this.getStopGaps();

        super.onEnter();

        Components.footer.showMiscWin("bonus_reels");
    }

    protected showRetriggerPrompt() {
        Services.sound.customEvent("retrigger");

        const content = new PromptContent(["bonus_freespinswon"], 2000);
        return Components.prompt.showPrompt(content);
    }

    protected currentSpinIsComplete() {
        const currentHoldAndSpin = gameState.getCurrentGame().getCurrentRecord() as HoldAndSpinRecord;
        return currentHoldAndSpin.complete;
    }

    protected shouldShowRetriggerPrompt() {
        const currentHoldAndSpin = gameState.getCurrentGame().getCurrentRecord() as HoldAndSpinRecord;
        return currentHoldAndSpin.issuesRetrigger;
    }

    protected startSpinning(): Contract<any> {
        if (!this.currentSpinIsComplete()) {
            Components.betBar.setFreespinsPlayed(getCurrentPick() + 1);
        }

        Services.sound.customEvent("fortune_blast_spin");

        return new Parallel([
            () => Contract.wrap(() => Services.sound.event(SoundEvent.start_freespin)),
            () => Components.matrix.startTransition(false, settingsModel.getQuickSpin()),
            () => new Sequence([
                () => LoadPickBonusCommand(0),
                () => Contract.wrap(() => gameState.getCurrentGame().latestRecord())
            ])
        ]);
    }

    protected newGame() {
        // Empty.
    }

    protected getStopGaps() {
        const columnHeight = 3;
        const stopGap = 370;
        let currentGap = 0;

        return Components.matrix.getGridSymbols().map((symbol, index) => {

            if (isMcSymbol(symbol.symbolId)) {
                return 0;
            }

            const columnIndex = Math.floor(index / columnHeight);
            const isFirstMovingInColumn = this.isFirstMovingSymbolInColumn(columnIndex, index);
            return isFirstMovingInColumn ? currentGap += stopGap : currentGap;
        });
    }

    private holdCurrentSymbols() {
        const currentSymbols = Components.matrix.getGridSymbols();
        const currentIndexes = this.getCurrentlyHeldReelIndexs();

        currentSymbols.forEach((symbol, index) => {
            if (isMcSymbol(symbol.symbolId)) {
                currentIndexes.push(index);
            }
        });

        Components.matrix.hold(currentIndexes);
    }

    private shouldHoldCurrentSymbols() {

        const rootRecord = gameState.getCurrentGame().getRootRecord() as MCSpinRecord;

        // Always hold on first bonus.
        if (!this.isFirstPickBonus()) {
            return true;
        }

        // For gateway, we only discard the first spin, and hold all subsequent spins.
        const bonusResults = rootRecord.getResultsOfType(BonusResult);
        const mcGatewayResult = bonusResults.filter((result) => result.id === "gateway").pop();
        const isFirstPickRequest = !mcGatewayResult.played;

        mcGatewayResult.played = true;

        return !isFirstPickRequest;
    }

    private isFirstPickBonus() {
        const rootRecord = gameState.getCurrentGame().getRootRecord() as MCSpinRecord;
        const bonusResults = rootRecord.getResultsOfType(BonusResult);
        const aPickHasCashWon = bonusResults.filter((result) => result.cashWon).length > 0;
        return aPickHasCashWon;
    }

    private isFirstMovingSymbolInColumn(columnIndex: number, symbolIndex: number) {
        const columnHeight = 3;
        const from = columnHeight * columnIndex;
        const previousSymbols = Components.matrix.getGridSymbols().slice(from, symbolIndex);
        return !previousSymbols.find((symbol) => !isMcSymbol(symbol.symbolId));
    }

    private getCurrentlyHeldReelIndexs() {
        const heldIndexes: number[] = [];

        Components.matrix.getGridSymbols().forEach((symbol, index) => {
            if (isMcSymbol(symbol.symbolId)) {
                heldIndexes.push(index);
            }
        });

        return heldIndexes;
    }
}
